import React, { useState, useEffect, ReactNode } from 'react';
import UnsupportedDevicePage from '../UnsupportedDevicePage';

interface DeviceGuardProps {
  /**
   * Children components to render when device is supported
   */
  children: ReactNode;
  /**
   * Minimum screen width in pixels (default: 1024)
   */
  minWidth?: number;
}

/**
 * Component that checks screen width and displays UnsupportedDevicePage
 * if screen width is below the minimum threshold
 * 
 * @param props - Component props
 * @returns Device guard component
 */
const DeviceGuard: React.FC<DeviceGuardProps> = ({
  children,
  minWidth = 1024,
}) => {
  const [isSupported, setIsSupported] = useState<boolean | null>(null);

  useEffect(() => {
    /**
     * Check if current screen width meets minimum requirement
     */
    const checkScreenWidth = () => {
      setIsSupported(window.innerWidth >= minWidth);
    };

    // Initial check
    checkScreenWidth();

    // Listen for resize events
    window.addEventListener('resize', checkScreenWidth);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkScreenWidth);
    };
  }, [minWidth]);

  // Show loading state while checking (optional, can be removed if not needed)
  if (isSupported === null) {
    return null; // Or return a loading spinner if preferred
  }

  // Show unsupported device page if screen is too small
  if (!isSupported) {
    return <UnsupportedDevicePage />;
  }

  // Render children if device is supported
  return <>{children}</>;
};

export default React.memo(DeviceGuard);

